import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, tap, withLatestFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LicenseDataService } from './data-services/license-data.service';
import { selectLicenseState } from './license.feature';
import { CoaterLicenseActions } from './license.actions';
import { ToastService } from '../../../shared';

@Injectable()
export class LicenseEffects {
  private actions$ = inject(Actions);
  private store$ = inject(Store);
  private toastService = inject(ToastService);
  private licenseDataService = inject(LicenseDataService);

  public loadLicenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoaterLicenseActions.loadCoaterLicenses),
      switchMap(({ pagination, filter, orderBy }) => {
        return this.licenseDataService.fetchLicenseNumbers(filter, orderBy, pagination).pipe(
          map(({ entities, annots }) => {
            const count = annots['annotations'].get('@odata.count');
            return CoaterLicenseActions.loadCoaterLicensesSuccess({ licenseNumbers: entities || [], count });
          }),
          catchError((error) => {
            return [CoaterLicenseActions.loadCoaterLicensesFailure({ error })];
          })
        );
      })
    )
  );
  public loadLicensesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoaterLicenseActions.loadCoaterLicensesFailure),
        tap(() => {
          this.toastService.displayErrorMessage('Licenses could not be loaded from the backend');
        })
      ),
    { dispatch: false }
  );
  public withdrawLicense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoaterLicenseActions.withdrawCoaterLicense),
      exhaustMap(({ coaterLicenseId, withdrawalDate, withdrawalReason }) => {
        return this.licenseDataService.withdrawLicense(coaterLicenseId, withdrawalDate, withdrawalReason).pipe(
          map(() => {
            return CoaterLicenseActions.withdrawCoaterLicenseSuccess();
          }),
          catchError((error) => {
            return [CoaterLicenseActions.withdrawCoaterLicenseFailure({ error })];
          })
        );
      })
    )
  );
  public withdrawLicenseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoaterLicenseActions.withdrawCoaterLicenseSuccess),
      tap(() => {
        this.toastService.displaySuccessMessage('Licenses withdrawn');
      }),
      withLatestFrom(this.store$.select(selectLicenseState)),
      exhaustMap(([_action, state]) => {
        const pagination = state.pagination;
        const filter = state.filter;
        const orderBy = state.orderBy;
        return [CoaterLicenseActions.loadCoaterLicenses({ pagination, filter, orderBy })];
      })
    )
  );
  public withdrawLicenseFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoaterLicenseActions.withdrawCoaterLicenseFailure),
        tap(() => {
          this.toastService.displayErrorMessage('Licenses could not be withdrawn');
        })
      ),
    { dispatch: false }
  );

  public grantLicense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoaterLicenseActions.grantCoaterLicense),
      exhaustMap(({ coaterLicenseId, expiryDate }) => {
        return this.licenseDataService.grantLicense(coaterLicenseId, expiryDate).pipe(
          map(() => {
            return CoaterLicenseActions.grantCoaterLicenseSuccess();
          }),
          catchError((error) => {
            return [CoaterLicenseActions.grantCoaterLicenseFailure({ error })];
          })
        );
      })
    )
  );
  public grantLicenseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoaterLicenseActions.grantCoaterLicenseSuccess),
      tap(() => {
        this.toastService.displaySuccessMessage('License granted');
      }),
      withLatestFrom(this.store$.select(selectLicenseState)),
      exhaustMap(([_action, state]) => {
        const pagination = state.pagination;
        const filter = state.filter;
        const orderBy = state.orderBy;
        return [CoaterLicenseActions.loadCoaterLicenses({ pagination, filter, orderBy })];
      })
    )
  );
  public grantLicenseFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoaterLicenseActions.grantCoaterLicenseFailure),
        tap(() => {
          this.toastService.displayErrorMessage('Licenses could not be granted');
        })
      ),
    { dispatch: false }
  );

  constructor() {}
}
